/**
 * 未收款清單 Receivables-Outstanding report (S11.1).
 *
 * Customer (AR) invoices with an open balance (應收 = 發票金額 − 已收金額),
 * with an overdue/aging flag (逾期) against the due date. Per-currency
 * outstanding totals only.
 */

import { useMemo } from 'react';
import { Card, Col, Row, Statistic, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import { formatAmount, formatMoney } from '@/components/salesOrders/components/soFormat';
import type { ArInvoice, ArInvoiceStatus } from '@/types/api/finance';
import { useReportArInvoices } from '../reportsData';
import { ReportSyncNote, ReportToolbar } from '../reportsShared';
import { exportReportCsv } from '../reportsCsv';

const { Text } = Typography;

interface ArRow extends ArInvoice {
  outstanding: number;
  daysOverdue: number;
}

const ArOutstandingReport = () => {
  const { t } = useTranslation(['reports', 'finance', 'common']);
  const { token } = theme.useToken();
  const { rows: invoices, isLoading, error, refresh } = useReportArInvoices();

  const { rows, generatedAt } = useMemo(() => {
    const today = dayjs().startOf('day');
    const derived: ArRow[] = invoices
      .map((invoice) => ({
        ...invoice,
        outstanding: invoice.amount - invoice.paidAmount,
        daysOverdue: today.diff(dayjs(invoice.dueDate).startOf('day'), 'day'),
      }))
      .filter((row) => row.outstanding > 0 && row.status !== 'void')
      .sort((a, b) => b.daysOverdue - a.daysOverdue);
    return { rows: derived, generatedAt: new Date().toISOString() };
  }, [invoices]);

  const totals = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((row) => map.set(row.currency, (map.get(row.currency) ?? 0) + row.outstanding));
    return [...map.entries()].map(([currency, amount]) => ({ currency, amount }));
  }, [rows]);
  const overdueCount = useMemo(() => rows.filter((row) => row.daysOverdue > 0).length, [rows]);

  const agingTag = (daysOverdue: number) =>
    daysOverdue > 0 ? (
      <Tag color="red">{t('reports:reports.aging.overdue', { days: daysOverdue })}</Tag>
    ) : (
      <Tag color="default">{t('reports:reports.aging.current', { days: -daysOverdue })}</Tag>
    );

  const columns: TableProps<ArRow>['columns'] = [
    {
      title: t('reports:reports.receivables.columns.invoice'),
      key: 'invoice',
      width: 160,
      fixed: 'left',
      render: (_: unknown, row) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>{row.invoiceNumber}</span>
      ),
    },
    {
      title: t('reports:reports.receivables.columns.customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 220,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('reports:reports.receivables.columns.so'),
      dataIndex: 'soNumber',
      key: 'soNumber',
      width: 150,
      render: (value: string | undefined) => renderDash(value),
    },
    {
      title: t('reports:reports.receivables.columns.amount'),
      key: 'amount',
      width: 140,
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(row.currency, row.amount)}
        </span>
      ),
    },
    {
      title: t('reports:reports.receivables.columns.paid'),
      key: 'paid',
      width: 140,
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(row.currency, row.paidAmount)}
        </span>
      ),
    },
    {
      title: t('reports:reports.receivables.columns.outstanding'),
      key: 'outstanding',
      width: 140,
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: token.fontWeightStrong }}>
          {formatMoney(row.currency, row.outstanding)}
        </span>
      ),
    },
    {
      title: t('reports:reports.receivables.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: ArInvoiceStatus) => (
        <Tag>{t(`finance:invoices.statusCustomer.${status}`)}</Tag>
      ),
    },
    {
      title: t('reports:reports.receivables.columns.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('reports:reports.receivables.columns.aging'),
      key: 'aging',
      width: 130,
      render: (_: unknown, row) => agingTag(row.daysOverdue),
    },
  ];

  const handleExport = () => {
    exportReportCsv<ArRow>(
      'receivables-outstanding.csv',
      [
        { header: t('reports:reports.receivables.columns.invoice'), value: (row) => row.invoiceNumber },
        { header: t('reports:reports.receivables.columns.customer'), value: (row) => row.customerName },
        { header: t('reports:reports.receivables.columns.so'), value: (row) => row.soNumber ?? '' },
        { header: t('reports:reports.common.currency'), value: (row) => row.currency },
        { header: t('reports:reports.receivables.columns.amount'), value: (row) => formatAmount(row.amount) },
        { header: t('reports:reports.receivables.columns.paid'), value: (row) => formatAmount(row.paidAmount) },
        { header: t('reports:reports.receivables.columns.outstanding'), value: (row) => formatAmount(row.outstanding) },
        { header: t('reports:reports.receivables.columns.status'), value: (row) => t(`finance:invoices.statusCustomer.${row.status}`) },
        { header: t('reports:reports.receivables.columns.dueDate'), value: (row) => row.dueDate },
        { header: t('reports:reports.receivables.columns.aging'), value: (row) => (row.daysOverdue > 0 ? t('reports:reports.aging.overdue', { days: row.daysOverdue }) : t('reports:reports.aging.current', { days: -row.daysOverdue })) },
      ],
      rows
    );
  };

  return (
    <>
      <ReportToolbar
        formula={t('reports:reports.receivables.formula')}
        onExport={handleExport}
        exportDisabled={rows.length === 0}
      />

      <Row gutter={[12, 12]} style={{ marginBottom: token.marginLG }}>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title={t('reports:reports.receivables.stats.openInvoices')}
              value={rows.length}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title={t('reports:reports.receivables.stats.overdue')}
              value={overdueCount}
              valueStyle={{ color: overdueCount > 0 ? token.colorError : undefined }}
            />
          </Card>
        </Col>
        {totals.map((entry) => (
          <Col xs={12} lg={6} key={entry.currency}>
            <Card size="small">
              <Statistic
                title={t('reports:reports.receivables.stats.outstanding', { currency: entry.currency })}
                value={formatAmount(entry.amount)}
              />
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {entry.currency}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>

      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('reports:reports.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<ArRow>
          rowKey="id"
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          locale={{ emptyText: <ListEmptyState title={t('reports:reports.receivables.empty')} /> }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1330 }}
        />
      </TableContainer>
      <ReportSyncNote syncedAt={generatedAt} />
    </>
  );
};

export default ArOutstandingReport;
